import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {trpc} from '@common-web/lib/trpc';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {Input} from '@common-web/components/ui/input';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {ArrowLeft, AlertCircle, Search, TableProperties} from 'lucide-react';
import type {RouterOutput} from '@common-web/lib/trpc';

type ReleaseNotes = NonNullable<
  RouterOutput['internal']['casPlanMappings']['getReleaseNotes']['notes']
>;
type ReleaseNoteBlock = ReleaseNotes['sections'][number]['blocks'][number];
/** Taken from the server's own list rather than restated here, so cutting a version cannot leave a
 *  stale copy of the union behind in this file. */
type PlanMappingVersionId =
  RouterOutput['internal']['casPlanMappings']['listVersions'][number]['id'];

/**
 * Renders one block of a release note. The block kinds mirror how the notes were written; the styling
 * keeps prose narrow and quiet so a long document stays readable, and reserves emphasis for the
 * lead-in labels ("The problem.", "Scale.") that carry the document's rhythm.
 */
function NoteBlock({block}: {block: ReleaseNoteBlock}) {
  switch (block.kind) {
    case 'callout':
      return (
        <div className='flex gap-3 rounded-xl bg-brand-blue/[0.06] border border-brand-blue/15 px-4 py-3.5'>
          <span className='w-1 rounded-full bg-brand-blue/60 shrink-0' />
          <p className='text-sm font-medium text-brand-navy leading-relaxed'>{block.text}</p>
        </div>
      );
    case 'bullets':
      return (
        <ul className='space-y-2 max-w-3xl'>
          {block.items.map((item) => (
            <li key={item} className='flex gap-2.5 text-sm text-slate-600 leading-relaxed'>
              <span className='mt-[7px] h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0' />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className='overflow-x-auto rounded-xl border border-slate-200 bg-white'>
          <table className='w-full text-sm'>
            <thead className='border-b border-slate-100'>
              <tr>
                {block.headers.map((h) => (
                  <th
                    key={h}
                    className='text-left px-4 py-2.5 text-[11px] uppercase tracking-wider font-medium text-slate-400'
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-100'>
              {block.rows.map((row) => (
                <tr key={row.join('|')}>
                  {row.map((cell, i) => (
                    <td
                      key={cell}
                      className={`px-4 py-2.5 ${i === 0 ? 'text-brand-navy font-medium' : 'text-slate-600 font-mono text-xs'}`}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'products':
      return (
        <div className='rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 sm:divide-y-0 sm:grid sm:grid-cols-2'>
          {block.items.map((p) => (
            <div key={p.productId} className='flex items-baseline gap-3 px-4 py-2.5'>
              <span className='font-mono text-xs text-slate-400 tabular-nums shrink-0'>
                {p.productId}
              </span>
              <span className='text-sm text-brand-navy'>{p.name}</span>
            </div>
          ))}
        </div>
      );
    default:
      return (
        <p className='text-sm text-slate-600 leading-relaxed max-w-3xl'>
          {block.label && <span className='font-semibold text-brand-navy'>{block.label} </span>}
          {block.text}
        </p>
      );
  }
}

/** The full release notes for a version, or a plain note when a version has none. */
function ReleaseNotesView({notes, loading}: {notes: ReleaseNotes | null; loading: boolean}) {
  if (loading) {
    return (
      <div className='space-y-3'>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className='h-4 w-full' />
        ))}
      </div>
    );
  }
  if (!notes) {
    return (
      <p className='text-sm text-slate-500'>No release notes were recorded for these versions.</p>
    );
  }
  return (
    <div>
      {/* Masthead: the headline plus the at-a-glance facts, as a single quiet card */}
      <div className='rounded-2xl border border-slate-200 bg-white p-6'>
        <h2 className='text-xl font-semibold text-brand-navy tracking-tight'>{notes.headline}</h2>
        <dl className='mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2'>
          {notes.facts.map((f) => (
            <div key={f.label}>
              <dt className='text-[11px] uppercase tracking-wider text-slate-400'>{f.label}</dt>
              <dd className='text-sm text-slate-600 mt-0.5 leading-relaxed'>{f.value}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Body: numbered rules between sections rather than boxes, so it reads as one document */}
      <div className='divide-y divide-slate-200'>
        {notes.sections.map((section) => (
          <section key={section.heading} className='py-8 space-y-4'>
            <h3 className='text-base font-semibold text-brand-navy tracking-tight'>
              {section.heading}
            </h3>
            {section.blocks.map((block, i) => (
              <NoteBlock key={i} block={block} />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

/**
 * Client-facing view of the CAS product → VBA plan mappings.
 *
 * Deliberately plain: one table, a version selector, and — for the current version — a group-type
 * selector, because two staffing brands resolve three products differently. Everything shown is read
 * from the same mapping module the sync applies, so it cannot describe a mapping that isn't real.
 */
export function VbaSyncCasMappings() {
  const navigate = useNavigate();
  // Null until the user picks one; the effective version falls back to whichever the server marks
  // CURRENT. Naming a version here meant the page kept opening on an archived mapping — in front of
  // clients — every time a new one was cut, and there is no reason for this file to know the ids.
  const [version, setVersion] = useState<PlanMappingVersionId | null>(null);
  const [groupType, setGroupType] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState('');

  const {
    data: versions,
    isLoading: versionsLoading,
    error: versionsError,
  } = trpc.internal.casPlanMappings.listVersions.useQuery();

  // Declared between the queries on purpose: it needs `versions`, and the two below need it.
  const activeVersion = version ?? versions?.find((v) => v.status === 'current')?.id ?? null;

  const {
    data: mapping,
    isLoading: mappingLoading,
    error: mappingError,
  } = trpc.internal.casPlanMappings.getMapping.useQuery(
    {version: activeVersion!, groupType},
    {enabled: activeVersion !== null},
  );

  const {data: releaseNotes, isLoading: notesLoading} =
    trpc.internal.casPlanMappings.getReleaseNotes.useQuery(
      {version: activeVersion!},
      {enabled: activeVersion !== null},
    );

  // A DISABLED query reports isLoading:false, so without this the table would flash
  // "No products in this version" while the version list is still resolving the default.
  const awaitingVersion = activeVersion === null;
  const showMappingSkeleton = mappingLoading || awaitingVersion;
  const selectedVersion = versions?.find((v) => v.id === activeVersion);
  const error = versionsError ?? mappingError;

  // Switching version resets the group type: v1's single list and v3's sections don't share keys.
  const chooseVersion = (id: PlanMappingVersionId) => {
    setVersion(id);
    setGroupType(undefined);
  };

  const term = search.trim().toLowerCase();
  const rows = (mapping?.rows ?? []).filter((r) => {
    if (!term) return true;
    return (
      r.productId.includes(term) ||
      (r.productName ?? '').toLowerCase().includes(term) ||
      r.planCodes.some((c) => c.toLowerCase().includes(term))
    );
  });

  return (
    <div className='min-h-screen bg-slate-50/70'>
      <div className='flex flex-col gap-0'>
        {/* Header — matches the runs page so the two read as one product */}
        <div className='bg-brand-navy'>
          <div className='max-w-5xl mx-auto px-8 py-8'>
            <div className='flex items-start justify-between gap-6 flex-wrap'>
              <div>
                <div className='flex items-center gap-3'>
                  <div className='p-2 rounded-xl bg-white/10'>
                    <TableProperties className='h-5 w-5 text-white' />
                  </div>
                  <h1 className='text-2xl font-semibold text-white tracking-tight'>
                    Plan Mappings & Release Notes
                  </h1>
                </div>
                <p className='text-slate-300/80 text-sm mt-2 max-w-xl'>
                  Which VBA plans each product enrolls a member into, and what changed in each
                  version of the sync.
                </p>
              </div>
              <Button
                variant='ghost'
                size='sm'
                className='text-white hover:bg-white/10 hover:text-white'
                onClick={() => navigate('/vba-sync-cas')}
              >
                <ArrowLeft className='h-4 w-4 mr-2' />
                Back to runs
              </Button>
            </div>
          </div>
        </div>

        <div className='px-8 py-8 max-w-5xl mx-auto w-full space-y-6'>
          {error ? (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle>Could not load plan mappings</AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Version selector — a segmented control, since the versions are one choice */}
              <div className='flex items-center gap-3 flex-wrap'>
                <div className='inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1'>
                  {versionsLoading
                    ? [0, 1].map((i) => <Skeleton key={i} className='h-8 w-24 rounded-lg' />)
                    : versions?.map((v) => {
                        const active = v.id === activeVersion;
                        return (
                          <button
                            key={v.id}
                            onClick={() => chooseVersion(v.id)}
                            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              active
                                ? 'bg-brand-navy text-white'
                                : 'text-slate-500 hover:text-brand-navy hover:bg-slate-50'
                            }`}
                          >
                            {v.label}
                            <span
                              className={`ml-2 text-[11px] ${active ? 'text-white/60' : 'text-slate-400'}`}
                            >
                              {v.status === 'current' ? 'Current' : 'Archived'}
                            </span>
                          </button>
                        );
                      })}
                </div>
                {selectedVersion && (
                  <p className='text-sm text-slate-500'>{selectedVersion.summary}</p>
                )}
              </div>

              <Tabs defaultValue='mappings' className='w-full'>
                <TabsList>
                  <TabsTrigger value='mappings'>Plan Mappings</TabsTrigger>
                  <TabsTrigger value='notes'>Release Notes</TabsTrigger>
                </TabsList>

                <TabsContent value='mappings' className='space-y-5 pt-4'>
                  {selectedVersion && selectedVersion.changes.length > 0 && (
                    <div className='rounded-2xl border border-brand-blue/20 bg-brand-blue/[0.04] p-5'>
                      <div className='text-sm font-semibold text-brand-navy mb-2.5'>
                        What changed in {selectedVersion.label}
                      </div>
                      <ul className='space-y-2 text-sm text-slate-600'>
                        {selectedVersion.changes.map((c) => (
                          <li key={c} className='flex gap-2.5'>
                            <span className='mt-2 h-1.5 w-1.5 rounded-full bg-brand-blue shrink-0' />
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Group type — only meaningful when a version has more than one */}
                  {selectedVersion && selectedVersion.groupTypes.length > 1 && (
                    <div className='space-y-2'>
                      <div className='inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1'>
                        {selectedVersion.groupTypes.map((g) => {
                          const active = (mapping?.groupType?.key ?? '') === g.key;
                          return (
                            <button
                              key={g.key}
                              onClick={() => setGroupType(g.key)}
                              className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                active
                                  ? 'bg-slate-100 text-brand-navy'
                                  : 'text-slate-500 hover:text-brand-navy hover:bg-slate-50'
                              }`}
                            >
                              {g.label}
                            </button>
                          );
                        })}
                      </div>
                      {mapping?.groupType && (
                        <p className='text-sm text-slate-500'>{mapping.groupType.description}</p>
                      )}
                    </div>
                  )}

                  {/* Search */}
                  <div className='relative max-w-md'>
                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400' />
                    <Input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder='Search by product, name, or plan code…'
                      className='pl-9 bg-white border-slate-200 rounded-xl'
                    />
                  </div>

                  {/* Mapping table */}
                  <div className='rounded-2xl border border-slate-200 bg-white overflow-hidden'>
                    <div className='overflow-x-auto'>
                      <table className='w-full text-sm'>
                        <thead className='border-b border-slate-100'>
                          <tr>
                            <th className='text-left px-5 py-3 text-[11px] uppercase tracking-wider font-medium text-slate-400'>
                              Product
                            </th>
                            <th className='text-left px-5 py-3 text-[11px] uppercase tracking-wider font-medium text-slate-400'>
                              VBA plans
                            </th>
                          </tr>
                        </thead>
                        <tbody className='divide-y divide-slate-100'>
                          {showMappingSkeleton ? (
                            [0, 1, 2, 3, 4].map((i) => (
                              <tr key={i}>
                                <td className='px-5 py-3.5'>
                                  <Skeleton className='h-4 w-40' />
                                </td>
                                <td className='px-5 py-3.5'>
                                  <Skeleton className='h-4 w-64' />
                                </td>
                              </tr>
                            ))
                          ) : rows.length === 0 ? (
                            <tr>
                              <td colSpan={2} className='px-5 py-12 text-center text-slate-400'>
                                {term
                                  ? `Nothing matches “${search.trim()}”`
                                  : 'No products in this version'}
                              </td>
                            </tr>
                          ) : (
                            rows.map((r) => (
                              <tr
                                key={r.productId}
                                className='align-top hover:bg-slate-50/80 transition-colors'
                              >
                                <td className='px-5 py-3.5'>
                                  <div className='font-medium text-brand-navy'>
                                    {r.productName ?? 'Unnamed product'}
                                  </div>
                                  <div className='text-xs font-mono text-slate-400 mt-0.5'>
                                    {r.productId}
                                  </div>
                                </td>
                                <td className='px-5 py-3.5'>
                                  {r.planCodes.length === 0 ? (
                                    <span className='text-xs text-slate-400 italic'>
                                      Not administered in VBA
                                    </span>
                                  ) : (
                                    <div className='flex flex-wrap gap-1.5'>
                                      {r.planCodes.map((c) => (
                                        <Badge
                                          key={c}
                                          variant='outline'
                                          className='font-mono text-xs font-normal border-slate-200 bg-slate-50 text-slate-600'
                                        >
                                          {c}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {!showMappingSkeleton && rows.length > 0 && (
                    <p className='text-xs text-brand-gray'>
                      {rows.length} product{rows.length === 1 ? '' : 's'}
                      {term ? ` matching “${search.trim()}”` : ''}
                    </p>
                  )}
                </TabsContent>

                <TabsContent value='notes' className='pt-4'>
                  <ReleaseNotesView
                    notes={releaseNotes?.notes ?? null}
                    loading={notesLoading || awaitingVersion}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
